import { useCallback, useEffect, useState } from "react";
import {
  clampDimension,
  loadDimension,
  saveDimension,
  type ScheduleDimension,
} from "@/lib/scheduleDimension";

export interface ScheduleDimensionValue {
  value: number;
  setValue: (value: number) => void;
  reset: () => void;
}

/**
 * React state for one adjustable schedule dimension (label-column width,
 * panel height, …), backed by localStorage per briefing and per scope.
 *
 * Stored state is `number | null` rather than the resolved size: null means
 * "never adjusted", so each view keeps its own default — and switching
 * briefings re-reads rather than carrying the previous production's size
 * over. The named hooks (`useScheduleLabelWidth`, `useSchedulePanelHeight`)
 * wrap this and only rename the returned fields.
 */
export function useScheduleDimension(
  dimension: ScheduleDimension,
  briefingId: string,
  scope: string,
  defaultValue: number,
): ScheduleDimensionValue {
  const [stored, setStored] = useState<number | null>(() =>
    loadDimension(dimension, briefingId, scope),
  );

  useEffect(() => {
    setStored(loadDimension(dimension, briefingId, scope));
  }, [dimension, briefingId, scope]);

  const setValue = useCallback(
    (value: number) => {
      const clamped = clampDimension(dimension, value);
      setStored(clamped);
      saveDimension(dimension, briefingId, scope, clamped);
    },
    [dimension, briefingId, scope],
  );

  const reset = useCallback(() => {
    setStored(null);
    saveDimension(dimension, briefingId, scope, null);
  }, [dimension, briefingId, scope]);

  return { value: stored ?? defaultValue, setValue, reset };
}
